import Axios from "../axios";
import {bellLogApiConstants} from "../constants/ApiConstant";
import {getRemoteBellUrl} from "../utils/BellSoundCache";

// Every time a bell plays we fire one log event at the backend. This must be a
// side effect that can NEVER break playback: all network work is done
// fire-and-forget and every failure is swallowed. When a request fails (offline,
// server down, etc.) the event is parked in a localStorage queue and flushed as
// a single batch on the next successful log / page load.

const OFFLINE_QUEUE_KEY = "bell:log-offline-queue";
// Cap the offline queue so a permanently-broken backend can't grow localStorage
// without bound. Oldest events are dropped first.
const OFFLINE_QUEUE_LIMIT = 200;

const readOfflineQueue = () => {
    try {
        const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const writeOfflineQueue = (events = []) => {
    try {
        const trimmed = events.slice(-OFFLINE_QUEUE_LIMIT);
        localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(trimmed));
    } catch (e) {
        // localStorage full / unavailable — nothing we can safely do, and this
        // is only a best-effort audit log, so we drop the event silently.
    }
};

const enqueueOffline = (event) => {
    const queue = readOfflineQueue();
    queue.push(event);
    writeOfflineQueue(queue);
};

// Build the wire payload from a bell object. schoolId and rosterId are derived
// on the backend from the authenticated token, so they are intentionally NOT
// sent here. Extra fields present on the bell/overrides are passed through so
// the backend gets whatever detail is available without the scheduler caring.
export const buildBellLogEvent = (bell = {}, overrides = {}) => {
    return {
        bellId: bell?.id ?? bell?.bellSetupId ?? null,
        bellName: bell?.name ?? null,
        bellType: bell?.type ?? null,
        hourTime: bell?.hourTime ?? null,
        playTime: bell?.playTime ?? null,
        // Resolved R2 audio URL for the bell's music (null for bundled/local sounds).
        bellMusicUrl: overrides?.bellMusicUrl ?? getRemoteBellUrl(bell) ?? bell?.fileUrl ?? bell?.url ?? null,
        status: overrides?.status ?? "PLAYED",
        source: overrides?.source ?? "SCHEDULER",
        // ISO timestamp of the actual play; falls back to now.
        playedAt: overrides?.playedAt ?? new Date().toISOString(),
    };
};

// Flush any events queued while offline as one batch request. Resolves to true
// when the queue was flushed (or was already empty), false when the flush
// failed and the events were kept for a later retry. Never throws.
export const flushBellLogQueue = async () => {
    const queue = readOfflineQueue();

    if (queue.length === 0) {
        return true;
    }

    try {
        await Axios.post(bellLogApiConstants.LOG_BELL_BATCH, {events: queue});
        writeOfflineQueue([]);
        return true;
    } catch (e) {
        // Keep the queue intact so we retry on the next play / load.
        return false;
    }
};

// Fire-and-forget log of a single bell play. Safe to call without awaiting;
// it always resolves and never rejects, so it cannot affect bell playback.
export const logBellPlay = async (bell = {}, overrides = {}) => {
    console.log("logBellPlay")
    const event = buildBellLogEvent(bell, overrides);

    // Opportunistically drain anything parked from earlier failures first, so a
    // recovered connection catches up before the fresh event goes out.
    await flushBellLogQueue();

    try {
        await Axios.post(bellLogApiConstants.LOG_BELL, event);
    } catch (e) {
        // Backend/network failed — park the event for the next batch flush.
        enqueueOffline(event);
    }
};

// Admin day-wise / filtered search, paginated and newest first.
export async function searchBellLogs(request = {}, page = 0, size = 20) {
    return Axios.get(bellLogApiConstants.SEARCH_BELL_LOG, {
        params: {...request, page, size},
    });
}

// Realtime feed of the latest bell events for the logged-in school.
export async function fetchLiveBellLogs(rosterId = null, limit = 20) {
    const params = {limit};
    if (rosterId != null) {
        params.rosterId = rosterId;
    }
    return Axios.get(bellLogApiConstants.LIVE_BELL_LOG, {params});
}

// Leaderboard: top schools by bells played for a period (DAILY/WEEKLY/MONTHLY/
// YEARLY) anchored at `date`. Returns rows of
// { schoolId, schoolName, schoolLogo, schoolAddress, bellsPlayed }.
export async function fetchTopSchools(limit = 10, period = null, date = null) {
    const params = {limit};
    if (period) params.period = period;
    if (date) params.date = date;
    return Axios.get(bellLogApiConstants.TOP_SCHOOLS, {params});
}
